/**
 * TOTP (Time-based One-Time Password) generator
 * RFC 6238 implementation
 */

function base32Decode(base32: string): Uint8Array {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  const cleanInput = base32.toUpperCase().replace(/=+$/, '');

  let bits = 0;
  let value = 0;
  const output: number[] = [];

  for (let i = 0; i < cleanInput.length; i++) {
    const idx = alphabet.indexOf(cleanInput[i]);
    if (idx === -1) continue;

    value = (value << 5) | idx;
    bits += 5;

    if (bits >= 8) {
      output.push((value >>> (bits - 8)) & 0xff);
      bits -= 8;
    }
  }

  return new Uint8Array(output);
}

async function hmacSha1(key: Uint8Array, message: Uint8Array): Promise<Uint8Array> {
  // @ts-expect-error key is a Uint8Array
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key,
    { name: 'HMAC', hash: 'SHA-1' },
    false,
    ['sign'],
  );

  // @ts-expect-error cryptoKey is a CryptoKey
  const signature = await crypto.subtle.sign('HMAC', cryptoKey, message);
  return new Uint8Array(signature);
}

function dynamicTruncate(hmac: Uint8Array): number {
  const offset = hmac[hmac.length - 1] & 0x0f;
  const binary =
    ((hmac[offset] & 0x7f) << 24) |
    ((hmac[offset + 1] & 0xff) << 16) |
    ((hmac[offset + 2] & 0xff) << 8) |
    (hmac[offset + 3] & 0xff);

  return binary % 1000000;
}

/**
 * Generate a TOTP code
 * @param secret - Base32-encoded secret key
 * @param timeStep - Time step in seconds (default: 30)
 * @returns 6-digit TOTP code
 */
export async function generateTOTP(secret: string, timeStep = 30): Promise<string> {
  const key = base32Decode(secret);
  const epoch = Math.floor(Date.now() / 1000);
  const counter = Math.floor(epoch / timeStep);

  // Convert counter to 8-byte buffer
  const buffer = new ArrayBuffer(8);
  const view = new DataView(buffer);
  view.setUint32(4, counter, false);

  const hmac = await hmacSha1(key, new Uint8Array(buffer));
  const code = dynamicTruncate(hmac);

  return code.toString().padStart(6, '0');
}

/**
 * Get remaining seconds until next TOTP rotation
 * @param timeStep - Time step in seconds (default: 30)
 * @returns Remaining seconds
 */
export function getTOTPTimeRemaining(timeStep = 30): number {
  const epoch = Math.floor(Date.now() / 1000);
  return timeStep - (epoch % timeStep);
}
